import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { Add, Remove } from '@material-symbols-svg/react/rounded/w500';
import { useEffect, useRef, useState } from 'react';

import { formatHM, parseHM } from '../../common';
import { MONO_FONT, palette, tokens } from '../../theme';

export type HoursInputProps = {
    hours: number;
    onChange: (hours: number) => void;
    disabled?: boolean;
    over?: boolean;
    step?: number;
    min?: number;
    ariaLabel?: string;
};

const HOLD_DELAY_MS = 350;
const HOLD_INTERVAL_MS = 60;

export const HoursInput: React.FC<HoursInputProps> = ({ hours, onChange, disabled, over, step = 0.25, min = 0, ariaLabel }) => {
    const classes = useStyles();
    const [text, setText] = useState(() => formatHM(hours));
    const [focused, setFocused] = useState(false);

    // Tracks the latest committed value so press-and-hold ticks read
    // the value we just emitted in this same tick — React props haven't
    // re-flowed yet, so `hours` would be stale inside setInterval.
    const hoursRef = useRef(hours);
    useEffect(() => {
        hoursRef.current = hours;
    }, [hours]);

    useEffect(() => {
        if (!focused) {
            setText(formatHM(hours));
        }
    }, [hours, focused]);

    const commit = () => {
        const parsed = parseHM(text);
        if (parsed != null && Math.abs(parsed - hours) > 0.001) {
            onChange(Math.max(min, parsed));
        } else {
            setText(formatHM(hours));
        }
    };

    const bump = (delta: number): boolean => {
        const next = Math.max(min, hoursRef.current + delta);
        if (Math.abs(next - hoursRef.current) < 0.001) {
            return false;
        }
        hoursRef.current = next;
        // Spinner clicks happen while focused; the formatted-on-blur
        // sync path won't fire, so push the new value into the local
        // text state explicitly.
        setText(formatHM(next));
        onChange(next);
        return true;
    };

    const holdRef = useRef<{ timeoutId?: number; intervalId?: number }>({});
    const stopHold = () => {
        if (holdRef.current.timeoutId != null) {
            window.clearTimeout(holdRef.current.timeoutId);
        }
        if (holdRef.current.intervalId != null) {
            window.clearInterval(holdRef.current.intervalId);
        }
        holdRef.current = {};
    };
    const startHold = (delta: number) => {
        stopHold();
        if (!bump(delta)) {
            return;
        }
        holdRef.current.timeoutId = window.setTimeout(() => {
            holdRef.current.intervalId = window.setInterval(() => {
                if (!bump(delta)) {
                    stopHold();
                }
            }, HOLD_INTERVAL_MS);
        }, HOLD_DELAY_MS);
    };

    useEffect(() => {
        return () => {
            if (holdRef.current.timeoutId != null) {
                window.clearTimeout(holdRef.current.timeoutId);
            }
            if (holdRef.current.intervalId != null) {
                window.clearInterval(holdRef.current.intervalId);
            }
        };
    }, []);

    return (
        <div
            className={mergeClasses(
                classes.wrapper,
                focused && classes.wrapperFocused,
                over && classes.wrapperOver,
                disabled && classes.wrapperDisabled,
            )}
        >
            <input
                type="text"
                inputMode="numeric"
                className={classes.input}
                aria-label={ariaLabel}
                value={text}
                disabled={disabled}
                onChange={(e) => setText(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => {
                    setFocused(false);
                    commit();
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        (e.target as HTMLInputElement).blur();
                    } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        bump(step);
                    } else if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        bump(-step);
                    }
                }}
            />
            <div className={mergeClasses(classes.spinners, focused && classes.spinnersVisible)}>
                <button
                    type="button"
                    aria-label="Increase"
                    className={classes.spinBtn}
                    tabIndex={-1}
                    disabled={disabled}
                    onPointerDown={(e) => {
                        e.preventDefault();
                        e.currentTarget.setPointerCapture(e.pointerId);
                        startHold(step);
                    }}
                    onPointerUp={stopHold}
                    onPointerCancel={stopHold}
                >
                    <Add className={classes.spinIcon} />
                </button>
                <button
                    type="button"
                    aria-label="Decrease"
                    className={classes.spinBtn}
                    tabIndex={-1}
                    disabled={disabled || hours <= min}
                    onPointerDown={(e) => {
                        e.preventDefault();
                        e.currentTarget.setPointerCapture(e.pointerId);
                        startHold(-step);
                    }}
                    onPointerUp={stopHold}
                    onPointerCancel={stopHold}
                >
                    <Remove className={classes.spinIcon} />
                </button>
            </div>
        </div>
    );
};

const useStyles = makeStyles({
    wrapper: {
        display: 'inline-flex',
        alignItems: 'center',
        height: '28px',
        backgroundColor: palette.panelBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: tokens.borderRadiusMedium,
        overflow: 'hidden',
        transition: 'border-color 160ms ease, background-color 160ms ease',
    },
    wrapperFocused: {
        border: `1px solid ${palette.accent}`,
    },
    wrapperOver: {
        border: `1px solid ${palette.statusOverAccent}`,
        backgroundColor: palette.statusOverBg,
        color: palette.statusOverText,
    },
    wrapperDisabled: {
        opacity: 0.5,
    },
    input: {
        width: '54px',
        height: '100%',
        padding: '0 6px',
        fontFamily: MONO_FONT,
        fontVariantNumeric: 'tabular-nums',
        fontSize: '14px',
        fontWeight: tokens.fontWeightBold,
        color: 'inherit',
        textAlign: 'right',
        backgroundColor: 'transparent',
        border: 'none',
        outline: 'none',
        '&:disabled': {
            cursor: 'not-allowed',
        },
    },
    spinners: {
        display: 'none',
        flexDirection: 'column',
        justifyContent: 'center',
        height: '100%',
        paddingRight: '2px',
    },
    spinnersVisible: {
        display: 'flex',
    },
    spinBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '16px',
        height: '12px',
        padding: 0,
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: tokens.colorNeutralForeground2,
        '&:hover:not(:disabled)': {
            color: tokens.colorNeutralForeground1,
        },
        '&:disabled': {
            opacity: 0.3,
            cursor: 'not-allowed',
        },
    },
    spinIcon: {
        width: '14px',
        height: '14px',
    },
});
